import React, { useState, useCallback, useMemo, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { toast as sonnerToast } from 'sonner';
import { AuthProvider, useAuth } from './auth/AuthContext';

// Dev-only: expose Sonner on window so we can fire a test toast from the
// browser console. Tree-shaken out of prod by Vite when import.meta.env.DEV
// is false. Use: __toast.success("hi"), __toast.error("oops"), etc.
if (import.meta.env.DEV && typeof window !== 'undefined') {
    window.__toast = sonnerToast;
}
import { StoreProvider, useStore, NavContext } from './store/StoreContext';
import { registerServiceWorker } from './lib/pwa';
import LoginScreen from './auth/LoginScreen';
import SetPasswordScreen from './auth/SetPasswordScreen';
import SetupFromLinkScreen from './auth/SetupFromLinkScreen';
import LoadingScreen from './components/ui/LoadingScreen';
import QuickPriceTool from './components/toolbar/QuickPriceTool';
import ErrorScreen from './components/ui/ErrorScreen';
import { Button, AppToaster, ErrorState } from './components/ui';
import {
    DashboardSkeleton,
    ClientsSkeleton,
    EstimatesSkeleton,
    DispatchSkeleton,
    GenericPageSkeleton,
} from './components/ui/PageSkeletons';
import Dashboard from './pages/Dashboard';
import DispatchPage from './components/dispatch/DispatchPage';
import ClientsPage from './pages/ClientsPage';
import EstimatesPage from './pages/EstimatesPage';
import ActiveMovesPage from './pages/ActiveMovesPage';
import RecordsPage from './pages/RecordsPage';
import CapacityPage from './pages/CapacityPage';
import CrewPage from './pages/CrewPage';
import FleetPage from './pages/FleetPage';
import StoragePage, { IconPackage } from './pages/StoragePage';
import SettingsPage from './pages/SettingsPage';
import AuditPage from './pages/AuditPage';
import RecapsPage from './pages/RecapsPage';
import PayrollPage from './pages/PayrollPage';
import WalkthroughPortal from './pages/WalkthroughPortal';
import CrewPortalPage from './pages/CrewPortalPage';
import CrewSwapNotice from './components/dispatch/CrewSwapNotice';
import {
    IconHome, IconGrid, IconClipboard, IconFileText, IconCalendar,
    IconArchive, IconUsers, IconTruck, IconSettings, IconLogOut, IconMenu,
    IconActivity, IconGauge, IconClock, IconDollarSign, IconSun, IconMoon, IconMonitor,
} from './components/icons';
import db from './lib/database';
import { useTheme } from './lib/theme';
import beatyLogo from './assets/beaty-logo.svg';

class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, info) {
        console.error('React ErrorBoundary caught:', error, info);
        // Forward to Sentry so we see crashes that hit users in production.
        // captureException tolerates being called when Sentry is disabled (dev), so no env check needed.
        Sentry.captureException(error, {
            contexts: { react: { componentStack: info.componentStack } },
        });
    }
    render() {
        if (this.state.hasError) {
            // feedback.md 4B: the same dashed error card panels use.
            return (
                <div className="max-w-xl mx-auto px-4 py-20">
                    <ErrorState
                        title="Something went wrong"
                        hint="The screen hit an error while drawing. Your saved work is safe — reload to pick up where you left off."
                        detail={this.state.error?.message || 'Unknown error'}
                        onRetry={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
                        retryLabel="Reload page"
                        showReload={false}
                    />
                </div>
            );
        }
        return this.props.children;
    }
}

function AppInner() {
    const { user, userRole, isAdmin, can, needsPassword, authLoading, signOut, saveThemePref } = useAuth();
    const { pref: themePref } = useTheme();
    const { loading, error, retryInfo, loadAll } = useStore();
    const validPages = ['dashboard','clients','estimates','dispatch','moves','capacity','records','crew','fleet','storage','recaps','payroll','activity','settings','walkthrough'];
    const getPageFromHash = () => {
        const hash = window.location.hash.replace('#', '');
        return validPages.includes(hash) ? hash : 'dashboard';
    };
    const [currentPage, setCurrentPageRaw] = useState(getPageFromHash);
    const setCurrentPage = useCallback((page) => {
        setCurrentPageRaw(page);
        window.location.hash = page;
    }, []);
    // External hash changes (typed URL, bookmark, back/forward, shared
    // link) now switch pages too — previously only in-app clicks did
    // (live test 2026-07-06: navigating to /#estimates left the app on
    // its current page). Same-value sets are no-ops, so no loop with
    // setCurrentPage writing the hash.
    useEffect(() => {
        const onHashChange = () => setCurrentPageRaw(getPageFromHash());
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [dispatchFocusDate, setDispatchFocusDate] = useState(null);
    const [openClientId, setOpenClientId] = useState(null);
    const [openEstimateId, setOpenEstimateId] = useState(null);

    const navigateToDispatch = useCallback((focusDate) => {
        setDispatchFocusDate(focusDate || null);
        setCurrentPage('dispatch');
    }, []);

    const navigateToClient = useCallback((clientId) => {
        setOpenClientId(clientId);
        setCurrentPage('clients');
    }, []);

    // Dashboard "New Client" → open the intake form in ONE click. The old
    // approach navigated then fake-clicked the page's button on a 100ms
    // timer — a race that missed on slow renders (tester report,
    // 2026-07-02). Same flag pattern as openClientId.
    const [openNewClient, setOpenNewClient] = useState(false);
    // Optional target move date carried alongside the flag — the Capacity
    // page's "Book this day" passes the clicked date so the intake form
    // opens pre-aimed at it (2026-07-29).
    const [newClientDate, setNewClientDate] = useState(null);
    const navigateToNewClient = useCallback((date = null) => {
        setNewClientDate(typeof date === 'string' ? date : null);
        setOpenNewClient(true);
        setCurrentPage('clients');
    }, []);

    // Navigate to Estimates page and auto-expand a specific estimate row.
    // Used by ClientIntakeModal → Estimates tab when the operator clicks an
    // estimate card, so it opens in the full page view instead of inline in
    // the client modal.
    const navigateToEstimate = useCallback((estimateId) => {
        setOpenEstimateId(estimateId);
        setCurrentPage('estimates');
    }, []);

    const navValue = useMemo(() => ({
        setCurrentPage, navigateToDispatch, navigateToClient, navigateToEstimate,
        openClientId, clearOpenClient: () => setOpenClientId(null),
        openEstimateId, clearOpenEstimate: () => setOpenEstimateId(null),
        navigateToNewClient, openNewClient, newClientDate, clearOpenNewClient: () => { setOpenNewClient(false); setNewClientDate(null); },
        dispatchFocusDate, clearDispatchFocus: () => setDispatchFocusDate(null)
    }), [navigateToDispatch, navigateToClient, navigateToEstimate, navigateToNewClient, openClientId, openEstimateId, openNewClient, newClientDate, dispatchFocusDate]);

    const [notifications, setNotifications] = useState([]);
    const [dbNotifications, setDbNotifications] = useState([]);  // persisted rows from notifications table
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    // Top-bar tools (2026-09-11) — Quick Price is the first; follows you to
    // every page. Pricing-visible roles only. P toggles it.
    const [showQuickPrice, setShowQuickPrice] = useState(false);
    // Header date — rendered once per mount; a session that spans midnight
    // picks up the new day on the next navigation/render.
    const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const addNotification = useCallback((msg, type = 'info') => {
        const id = Date.now() + Math.random();
        setNotifications(prev => [{ id, msg, type, time: new Date(), read: false }, ...prev.slice(0, 49)]);
    }, []);

    // Fetch persisted notifications + poll every 30s
    const loadDbNotifications = useCallback(async () => {
        try {
            const rows = await db.listNotifications({ limit: 30 });
            setDbNotifications(rows || []);
        } catch (e) {
            // Silent fail — table may not exist yet on fresh installs
        }
    }, []);
    useEffect(() => {
        loadDbNotifications();
        const interval = setInterval(loadDbNotifications, 30000);
        return () => clearInterval(interval);
    }, [loadDbNotifications]);

    const dbUnreadCount = dbNotifications.filter(n => !n.read_at).length;
    const sessionUnreadCount = notifications.filter(n => !n.read).length;
    const unreadCount = dbUnreadCount + sessionUnreadCount;

    const markAllRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        if (dbUnreadCount > 0) {
            try {
                await db.markAllNotificationsRead();
                setDbNotifications(prev => prev.map(n => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
            } catch (e) { /* non-fatal */ }
        }
    };

    const handleNotifClick = (notif) => {
        if (notif.link && notif.link.startsWith('#')) {
            const page = notif.link.replace('#', '');
            if (validPages.includes(page)) setCurrentPage(page);
        }
        setShowNotifPanel(false);
    };

    useEffect(() => {
        const handleKeyboard = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
            if (e.target.isContentEditable) return;
            if (e.metaKey || e.ctrlKey) return;
            // Any open modal/dialog/drawer suspends page shortcuts entirely
            // (QA M2 BUG-7): a mis-click that dropped focus to the body plus
            // a typed digit ("200" into a pricing form) was silently
            // navigating pages, unmounting the modal, and destroying the
            // draft with no unsaved-changes guard. Modal + ConfirmDialog
            // render role="dialog"; Vaul drawers carry the vaul-drawer attr.
            if (document.querySelector('[role="dialog"], [vaul-drawer], [data-vaul-drawer]')) return;
            const shortcuts = { '1': 'dashboard', '2': 'dispatch', '3': 'clients', '4': 'estimates', '5': 'moves', '6': 'records', '7': 'crew', '8': 'fleet', '9': 'settings' };
            if (shortcuts[e.key]) { e.preventDefault(); setCurrentPage(shortcuts[e.key]); }
            if (e.key === 'b' || e.key === 'B') { e.preventDefault(); setSidebarOpen(prev => !prev); }
            if (e.key === 'n' || e.key === 'N') { e.preventDefault(); setShowNotifPanel(prev => !prev); }
            if (e.key === 'p' || e.key === 'P') { e.preventDefault(); setShowQuickPrice(prev => !prev); }
            if (e.key === '?' && e.shiftKey) { e.preventDefault(); addNotification('Keyboard shortcuts: 1-9 = navigate pages, B = toggle sidebar, N = notifications, P = billing calculator, ? = this help', 'info'); }
        };
        document.addEventListener('keydown', handleKeyboard);
        return () => document.removeEventListener('keydown', handleKeyboard);
    }, [addNotification]);

    // Errors still get the full-screen treatment — they need the user's
    // attention. But the loading state now flows into the page chrome
    // below: the sidebar/header render normally and the content area
    // shows a route-shaped skeleton (see `pageContent` below). This
    // gives users something page-shaped to look at instead of a red
    // void, and prevents the layout shift that happens when
    // LoadingScreen unmounts and the real page mounts.
    if (error) return <ErrorScreen message={error} onRetry={loadAll} retryInfo={retryInfo} />;

    // Full-screen walkthrough portal (no sidebar/header). Gated on the
    // use_walkthrough_portal capability — anyone without it gets bounced
    // back to the dashboard. Users seeing this surface intentionally
    // navigated to #walkthrough so we don't need an apology screen.
    if (currentPage === 'walkthrough') {
        if (!can('use_walkthrough_portal')) {
            return (
                <ErrorScreen
                    message="You don't have permission to use the walkthrough portal."
                    onRetry={() => setCurrentPage('dashboard')}
                />
            );
        }
        return (
            <NavContext.Provider value={navValue}>
                <WalkthroughPortal onExit={() => setCurrentPage('dashboard')} />
            </NavContext.Provider>
        );
    }

    const userEmail = user.email || '';
    const userInitials = userEmail.split('@')[0].slice(0, 2).toUpperCase();
    // Nav items declare a capability they require (if any). The render loop
    // hides any nav item the current user doesn't satisfy. See
    // src/auth/capabilities.js for the canonical capability catalog.
    const allNavItems = [
        { id: 'dashboard', label: 'Dashboard', icon: IconHome },
        { id: 'clients',   label: 'Clients',   icon: IconClipboard, capability: 'view_clients' },
        { id: 'estimates', label: 'Estimates', icon: IconFileText,  capability: 'view_estimates' },
        { id: 'dispatch',  label: 'Dispatch',  icon: IconGrid,      capability: 'view_dispatch' },
        { id: 'capacity',  label: 'Capacity',  icon: IconGauge,     capability: 'view_dispatch' },
        { id: 'moves',     label: 'Active Moves', icon: IconCalendar, capability: 'view_dispatch' },
        { id: 'storage',   label: 'Storage',   icon: IconPackage,   capability: 'view_clients' },
        { id: 'records',   label: 'Records',   icon: IconArchive,   capability: 'view_records' },
        { id: 'crew',      label: 'Crew',      icon: IconUsers,     capability: 'manage_crew' },
        { id: 'fleet',     label: 'Fleet',     icon: IconTruck,     capability: 'manage_crew' },
        { id: 'recaps',    label: 'Recaps',    icon: IconClock,     capability: 'view_records' },
        { id: 'payroll',   label: 'Payroll',   icon: IconDollarSign, capability: 'view_financials' },
        { id: 'activity',  label: 'Activity',  icon: IconActivity,  capability: 'manage_settings' },
        { id: 'settings',  label: 'Settings',  icon: IconSettings,  capability: 'manage_settings' },
    ];
    const navItems = allNavItems.filter(item => !item.capability || can(item.capability));

    return (
        <NavContext.Provider value={navValue}>
            <div className="flex h-screen bg-slate-50">
                {/* Sidebar body #16181d — same charcoal as the Billing Calculator
                    device (Jaylen 2026-09-11: one dark tone across the app's chrome
                    instead of the navy). Hover/borders are the calculator's key tones. */}
                {sidebarOpen && (
                    <div className="theme-fixed w-60 bg-[var(--chrome)] text-white flex flex-col flex-shrink-0">
                        <div className="border-b border-black/40 bg-[#ed1c24] flex flex-col items-center pt-3 pb-2">
                            <img
                                src={beatyLogo}
                                alt="Beaty Brothers Moving"
                                className="w-36 h-36 block"
                            />
                            <p className="font-lemonmilk text-white text-[11px] tracking-[0.3em] uppercase -mt-2">System</p>
                        </div>
                        <nav className="flex-1 p-4 space-y-1">
                            {navItems.map(item => {
                                const Icon = item.icon;
                                return <button key={item.id} onClick={() => setCurrentPage(item.id)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors ${currentPage === item.id ? 'bg-red-600 text-white shadow-sm shadow-red-900/30' : 'text-slate-400 hover:bg-[var(--chrome-2)] hover:text-slate-200'}`}><Icon size={18}/><span className="text-sm font-medium">{item.label}</span></button>;
                            })}
                        </nav>
                        <div className="p-4 border-t border-white/10">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">{userInitials}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <p className="text-sm font-medium truncate flex-1 min-w-0">{userEmail.split('@')[0]}</p>
                                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap ${isAdmin ? 'bg-red-600/30 text-red-200' : 'bg-blue-600/30 text-blue-200'}`}>{userRole || '...'}</span>
                                    </div>
                                    <p className="text-xs text-slate-400 truncate">{userEmail}</p>
                                </div>
                            </div>
                            {/* Quick theme switch (theme.md, pick "Follow the device,
                                per-user override"): cycles Light → Dark → System and
                                saves to this user's profile. The full control with
                                labels is in Settings → Appearance. */}
                            <div className="flex items-center gap-1.5">
                                <button onClick={signOut} className="flex-1 flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-[var(--chrome-2)] rounded-lg text-sm"><IconLogOut size={16}/> Sign Out</button>
                                <button
                                    type="button"
                                    onClick={() => saveThemePref(themePref === 'light' ? 'dark' : themePref === 'dark' ? 'system' : 'light')}
                                    title={`Theme: ${themePref} — click to change`}
                                    aria-label={`Theme: ${themePref}. Click to change.`}
                                    className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-[var(--chrome-2)]"
                                >
                                    {themePref === 'light' ? <IconSun size={16}/> : themePref === 'dark' ? <IconMoon size={16}/> : <IconMonitor size={16}/>}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="h-14 bg-white border-b border-slate-100 flex items-center px-4 md:px-6 flex-shrink-0 relative">
                        {/* Today, dead-center in the bar (Jaylen 2026-09-11): the
                            header is becoming the home for cross-page tools, so the
                            date lives here rather than inside any one of them.
                            Absolutely centered so the breadcrumb (left) and the
                            tool cluster (right) can grow without nudging it. */}
                        <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 pointer-events-none select-none">
                            <span className="text-xs font-semibold text-slate-700 tracking-wide">{todayLabel}</span>
                        </div>
                        <button onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'} className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-500 hover:text-slate-700"><IconMenu size={20}/></button>
                        <div className="ml-3 flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-400 tracking-wide">BBM</span>
                            <span className="text-slate-200">/</span>
                            <span className="text-sm font-semibold text-slate-800 capitalize">{currentPage === 'moves' ? 'Active Moves' : currentPage === 'estimates' ? 'Estimates' : currentPage}</span>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            {/* Toolbar tools — present on every page. Add the next
                                one here (a trigger + its own panel component). */}
                            {can('view_estimate_pricing') && (
                                <QuickPriceTool open={showQuickPrice} onToggle={() => setShowQuickPrice(o => !o)} onClose={() => setShowQuickPrice(false)} />
                            )}
                            <div className="relative">
                                <button onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) markAllRead(); }} aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'} className="p-2 hover:bg-slate-50 rounded-lg text-slate-500 hover:text-slate-700 relative">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
                                    {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                                </button>
                                {showNotifPanel && (
                                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-96 overflow-auto">
                                        <div className="p-3 border-b border-slate-100 flex items-center justify-between"><span className="text-sm font-bold text-slate-800">Notifications</span><span className="text-[10px] text-slate-400">Press N to toggle</span></div>
                                        {dbNotifications.length === 0 && notifications.length === 0 ? (
                                            <div className="p-6 text-center text-slate-400 text-sm">No notifications yet</div>
                                        ) : (
                                            <>
                                                {/* Persisted notifications (deposit paid, terms signed, etc.) */}
                                                {dbNotifications.map(n => {
                                                    const dotColor = n.type === 'deposit_paid' ? 'bg-emerald-500'
                                                        : n.type === 'terms_signed' ? 'bg-emerald-500'
                                                        : n.type === 'estimate_viewed' ? 'bg-blue-500'
                                                        : 'bg-slate-400';
                                                    const isUnread = !n.read_at;
                                                    return (
                                                        <button
                                                            key={'db-' + n.id}
                                                            type="button"
                                                            onClick={() => handleNotifClick(n)}
                                                            className={`w-full text-left px-3 py-2.5 border-b border-slate-50 text-sm hover:bg-slate-50 transition-colors ${isUnread ? 'text-slate-800 bg-red-50/40' : 'text-slate-500'}`}
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dotColor}`}></span>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="font-semibold">{n.title}</div>
                                                                    {n.body && <div className="text-[11px] text-slate-500 mt-0.5">{n.body}</div>}
                                                                    <div className="text-[10px] text-slate-400 mt-0.5">
                                                                        {n.created_at ? new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                                {/* Session-only notifications (toasts, shortcuts help) */}
                                                {notifications.map(n => (
                                                    <div key={n.id} className={`px-3 py-2.5 border-b border-slate-50 text-sm ${n.read ? 'text-slate-500' : 'text-slate-800 bg-blue-50/50'}`}>
                                                        <div className="flex items-start gap-2">
                                                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${n.type === 'error' ? 'bg-red-500' : n.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'}`}></span>
                                                            <div><div>{n.msg}</div><div className="text-[10px] text-slate-400 mt-0.5">{n.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <span className="text-[10px] text-slate-400 hidden md:block" title="Press ? for keyboard shortcuts">&#9000; shortcuts</span>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto">
                        {loading ? (
                            // Route-shaped skeleton during initial data load.
                            // Pages we don't have a tailored skeleton for fall
                            // through to GenericPageSkeleton.
                            currentPage === 'dashboard' ? <DashboardSkeleton/> :
                            currentPage === 'dispatch'  ? <DispatchSkeleton/>  :
                            currentPage === 'clients'   ? <ClientsSkeleton/>   :
                            currentPage === 'estimates' ? <EstimatesSkeleton/> :
                            <GenericPageSkeleton/>
                        ) : (
                            <>
                                {currentPage === 'dashboard' && <Dashboard/>}
                                {currentPage === 'dispatch' && <DispatchPage/>}
                                {currentPage === 'clients' && <ClientsPage/>}
                                {currentPage === 'estimates' && <EstimatesPage/>}
                                {currentPage === 'moves' && <ActiveMovesPage/>}
                                {currentPage === 'capacity' && can('view_dispatch') && <CapacityPage/>}
                                {currentPage === 'storage' && can('view_clients') && <StoragePage/>}
                                {currentPage === 'records' && <RecordsPage/>}
                                {currentPage === 'crew' && can('manage_crew') && <CrewPage/>}
                                {currentPage === 'fleet' && can('manage_crew') && <FleetPage/>}
                                {currentPage === 'recaps' && can('view_records') && <RecapsPage/>}
                                {currentPage === 'payroll' && can('view_financials') && <PayrollPage/>}
                                {currentPage === 'activity' && can('manage_settings') && <AuditPage/>}
                                {currentPage === 'settings' && can('manage_settings') && <SettingsPage/>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </NavContext.Provider>
    );
}

function AuthGate() {
    const { user, authLoading, authError, retryAuth, needsPassword } = useAuth();

    const setupToken = (() => { try { return new URLSearchParams(window.location.search).get('setup'); } catch { return null; } })();
    if (setupToken && !user) return <SetupFromLinkScreen token={setupToken} />;
    // Connection trouble during the initial auth bootstrap — most
    // commonly a Supabase gateway outage. Surface as a real error
    // screen with a retry button instead of leaving the user on the
    // red LoadingScreen forever (the May 19 incident bug).
    if (authError) return (
        <ErrorScreen
            message={`We're having trouble reaching the server. ${authError}. This usually clears up on its own within a few minutes.`}
            onRetry={retryAuth}
        />
    );
    if (authLoading) return <LoadingScreen />;
    if (!user) return <LoginScreen />;
    if (needsPassword) return <SetPasswordScreen />;

    return (
        <StoreProvider>
            <AppInner />
            {/* Crew-swap notification prompt (Jaylen 2026-07-08) — global
                host so JobModal / AssignModal / board drags all funnel into
                one confirm-first dialog. Lives inside StoreProvider (reads
                swapNotice from the store). */}
            <CrewSwapNotice />
        </StoreProvider>
    );
}

export default function App() {
    // Offline shell (2026-08-26): register the service worker for EVERY
    // route, not just /crew — sw.js now runtime-caches the app shell +
    // CDN scripts, so the estimator (and office app) can BOOT with no
    // signal at a walkthrough. Registration is idempotent (pwa.js reuses
    // the in-flight promise) and the SW ignores /api/ + Supabase traffic.
    useEffect(() => {
        registerServiceWorker().catch(() => {});
    }, []);

    // Path-based crew portal route. Crew leads access /crew?token=X via the
    // daily SMS link and are NOT logged into Supabase — the token in the URL
    // is their entire auth surface. Short-circuit before AuthProvider /
    // StoreProvider mount so we don't trigger any session lookup or
    // global-data load on their behalf. The rest of the app continues to
    // hash-route as today; this is a single path-based leaf, not a
    // wholesale router migration. Vercel.json's catch-all rewrite already
    // serves index.html for /crew, so no infra change is needed for the
    // path to resolve.
    // startsWith covers the sub-leaves: /crew/setup (SMS invite landing,
    // sets the crew member's password) and any future /crew/* screens.
    if (typeof window !== 'undefined' && (window.location.pathname === '/crew' || window.location.pathname.startsWith('/crew/'))) {
        return (
            <ErrorBoundary>
                <CrewPortalPage />
                <AppToaster />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <AuthProvider>
                <AuthGate />
            </AuthProvider>
            {/* Sonner toast host — Emil-built, ships excellent defaults. We
                pass `richColors` so success/error variants get proper hues,
                and a className that aligns the visual weight with the rest
                of the app (Inter font is inherited via the global `*` rule).
                Position matches the prior toast (top-right). */}
            <AppToaster />
        </ErrorBoundary>
    );
}
